import { useRef, useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { Camera, Loader2, Trash2, UploadCloud } from "lucide-react";

import { Avatar } from "@/components/shared/Avatar";
import { Button } from "@/components/ui/button";
import { authApi, vertaalApiFout } from "@/lib/api";
import { useAuthStore } from "@/lib/auth-store";
import { cn } from "@/lib/utils";
import type { GebruikerAntwoord } from "@/types/api";

const MAX_BINARY_BYTES = 200 * 1024;
const TOEGESTAAN_MIME = new Set(["image/png", "image/jpeg"]);

/**
 * Upload + preview voor de profielfoto van de ingelogde gebruiker.
 *
 * Werkt met data URLs: de client leest de file als base64 data URL,
 * valideert mime-type + grootte, en stuurt 'm naar PUT /api/auth/me/avatar.
 * Server valideert opnieuw en slaat op in `gebruikers.avatar_url`.
 */
export function AvatarUpload({ gebruiker }: { gebruiker: GebruikerAntwoord }) {
  const { t } = useTranslation();
  const setGebruiker = useAuthStore((s) => s.setGebruiker);
  const qc = useQueryClient();
  const fileRef = useRef<HTMLInputElement>(null);
  const [bezigLezen, setBezigLezen] = useState(false);
  const [sleepActief, setSleepActief] = useState(false);

  const zetMutatie = useMutation({
    mutationFn: (dataUrl: string) => authApi.zetAvatar(dataUrl),
    onSuccess: (fresh) => {
      setGebruiker(fresh);
      qc.setQueryData(["auth", "me"], fresh);
      toast.success(t("profiel.avatar_opgeslagen"));
    },
    onError: (fout) => toast.error(vertaalApiFout(fout)),
  });

  const verwijderMutatie = useMutation({
    mutationFn: () => authApi.verwijderAvatar(),
    onSuccess: (fresh) => {
      setGebruiker(fresh);
      qc.setQueryData(["auth", "me"], fresh);
      toast.success(t("profiel.avatar_verwijderd"));
    },
    onError: (fout) => toast.error(vertaalApiFout(fout)),
  });

  function valideerEnUpload(file: File) {
    if (!TOEGESTAAN_MIME.has(file.type)) {
      toast.error(t("profiel.avatar_verkeerd_formaat"));
      return;
    }
    if (file.size > MAX_BINARY_BYTES) {
      toast.error(t("profiel.avatar_te_groot"));
      return;
    }
    setBezigLezen(true);
    const reader = new FileReader();
    reader.onload = () => {
      setBezigLezen(false);
      const resultaat = reader.result;
      if (typeof resultaat !== "string") {
        toast.error(t("algemeen.onbekende_fout"));
        return;
      }
      zetMutatie.mutate(resultaat);
    };
    reader.onerror = () => {
      setBezigLezen(false);
      toast.error(t("algemeen.onbekende_fout"));
    };
    reader.readAsDataURL(file);
  }

  function onSelect(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (file) valideerEnUpload(file);
  }

  function onDrop(e: React.DragEvent<HTMLDivElement>) {
    e.preventDefault();
    setSleepActief(false);
    const file = e.dataTransfer.files?.[0];
    if (file) valideerEnUpload(file);
  }

  const bezig = bezigLezen || zetMutatie.isPending || verwijderMutatie.isPending;

  const voor = (gebruiker.naam ?? "").split(" ")[0] ?? "";
  const achter = (gebruiker.naam ?? "").split(" ").slice(1).join(" ");

  return (
    <div className="flex flex-col sm:flex-row sm:items-center gap-5">
      <div className="relative flex-shrink-0">
        <Avatar
          naam={gebruiker.naam}
          voornaam={voor}
          achternaam={achter}
          avatarUrl={gebruiker.avatar_url}
          className="h-24 w-24"
          lettergrootte="text-[28px]"
          vorm="2xl"
        />
        {bezig && (
          <div className="absolute inset-0 rounded-2xl bg-black/40 grid place-items-center">
            <Loader2 className="h-6 w-6 animate-spin text-white" />
          </div>
        )}
      </div>

      <div className="min-w-0 flex-1 space-y-3">
        <div
          onDragOver={(e) => {
            e.preventDefault();
            setSleepActief(true);
          }}
          onDragLeave={() => setSleepActief(false)}
          onDrop={onDrop}
          role="button"
          tabIndex={0}
          onClick={() => fileRef.current?.click()}
          onKeyDown={(e) => {
            if (e.key === "Enter" || e.key === " ") fileRef.current?.click();
          }}
          className={cn(
            "flex items-center gap-3 rounded-lg border-2 border-dashed px-4 py-3 text-[13.5px] cursor-pointer transition-colors",
            sleepActief
              ? "border-brand-cyan bg-brand-cyan-soft"
              : "border-border hover:border-brand-cyan/50 hover:bg-muted/50",
          )}
          aria-label={t("profiel.avatar_sleep_hier")}
        >
          <UploadCloud className="h-4 w-4 text-muted-foreground" />
          <span className="text-muted-foreground">
            {t("profiel.avatar_sleep_hier")}
          </span>
        </div>

        <div className="flex gap-2">
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() => fileRef.current?.click()}
            disabled={bezig}
          >
            <Camera className="h-3.5 w-3.5" />
            {gebruiker.avatar_url
              ? t("profiel.avatar_vervang")
              : t("profiel.avatar_upload")}
          </Button>
          {gebruiker.avatar_url && (
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => verwijderMutatie.mutate()}
              disabled={bezig}
              className="text-destructive hover:text-destructive"
            >
              <Trash2 className="h-3.5 w-3.5" />
              {t("profiel.avatar_verwijder")}
            </Button>
          )}
        </div>
        <p className="text-[12px] text-muted-foreground">
          PNG of JPG · max 200 KB · vierkant wordt mooi rond getoond
        </p>
      </div>

      <input
        ref={fileRef}
        type="file"
        accept="image/png,image/jpeg"
        className="sr-only"
        onChange={onSelect}
      />
    </div>
  );
}
